import clsx from "clsx";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  GetAvailableMachinesForReplaceType,
  GetLocationsType,
} from "../../../models/_publicCall";
import Datetime from "react-datetime";
import { InstalltionFormSteps } from "../../../providers/InstallationFormProvider";
import {
  INSTALLATION_JOB_STORAGE_KEY,
  INSTALLATION_JOB_STEP_STORAGE_KEY,
} from "../InstallationJob";
import toast from "react-hot-toast";
import SimpleMobileStepper from "../../Stepper/Stepper";
import { InstallationJobStepsList } from "../InstallationSteps";
import { installationJobMachineSetup } from "../../../models/_publicCall";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { Modal } from "react-bootstrap";

interface MachineSetupProps {
  jobId: string;
  locationDB: GetLocationsType[];
  machinesDB: GetAvailableMachinesForReplaceType[];
  setCurrentStep: (step: number) => void;
  storedData: any;
  jobStatus: string;
  installationJobDB: any;
  isFormSubmitted: boolean;
  setIsFormSubmitted: (submitted: boolean) => void;
}

interface MachineEntry {
  id: number;
  selectedMachine: GetAvailableMachinesForReplaceType | null;
  selectedMachineId?: string;
  newMachineName: string;
  deploymentDate: string;
  deploymentDateError?: string;
  machineError?: string;
}

export const MachineSetup: React.FC<MachineSetupProps> = ({
  jobId,
  locationDB,
  machinesDB,
  setCurrentStep,
  storedData,
  jobStatus,
  installationJobDB,
  isFormSubmitted,
  setIsFormSubmitted,
}) => {
  const [machineEntries, setMachineEntries] = useState<MachineEntry[]>([]);
  const [localMachinesDB, setLocalMachinesDB] =
    useState<GetAvailableMachinesForReplaceType[]>(machinesDB);
  const [location, setLocation] = useState<string>("");
  const [thirdPartyEmail, setThirdPartyEmail] = useState("");
  const [thirdPartyName, setThirdPartyName] = useState("");
  const [thirdPartyEmailError, setThirdPartyEmailError] = useState("");
  const [thirdPartyNameError, setThirdPartyNameError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const statusMap: Record<string, string> = {
    "36588335-d898-4268-922c-d9ab3e93c54e": "New – Ready for Deployment",
    "e3e2b5bf-b308-4e45-827e-8dee135bdf5f":
      "Refurbished – Ready for Deployment",
  };
  const navigate = useNavigate();

  useEffect(() => {
    setLocalMachinesDB(machinesDB);
  }, [machinesDB]);

  /** Load saved data - prioritize localStorage over database */
  useEffect(() => {
    // Read directly from localStorage to always get latest values
    const savedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}"
    );

    const selectedLocationId = savedData.locationSelection?.id;
    const matchedLocation = locationDB.find(
      (loc) => loc.id === selectedLocationId
    );
    const locationName = matchedLocation?.name;
    const locationCity = matchedLocation?.city;
    let cleaned = `${locationName}-${locationCity}`
      .trim()
      .replace(/\s+/g, "-")
      .toLowerCase();
    setLocation(cleaned);

    if(savedData.thirdPartyDetail) {
      setThirdPartyEmail(savedData.thirdPartyDetail.email || "");
      setThirdPartyName(savedData.thirdPartyDetail.name || "");
    }

    if (savedData.machineSetup && savedData.machineSetup.length > 0) {
      const restoredEntries = savedData.machineSetup.map((entry: any) => {
        const machineId =
          entry.selectedMachineId ||
          entry.machineId ||
          entry.selectedMachine?.id;
        const machine =
          machinesDB.find((m) => m.id === machineId) ||
          entry.selectedMachine ||
          null;
        return {
          ...entry,
          selectedMachine: machine,
          selectedMachineId: machineId || entry.selectedMachineId,
        };
      });
      setMachineEntries(restoredEntries);
      return;
    }

    if (
      jobStatus !== "Completed" &&
      installationJobDB &&
      installationJobDB.machines &&
      installationJobDB.machines.length > 0
    ) {
      const restoredEntries = installationJobDB.machines.map(
        (m: any, idx: number) => {
          const machine =
            machinesDB.find((x) => String(x.id) === String(m.id)) || null;
          return {
            id: idx + 1,
            selectedMachine: machine,
            selectedMachineId: String(m.id),
            newMachineName: (
              m.newMachineName ||
              m.currentMachineName ||
              ""
            ).toLowerCase(),
            deploymentDate: m.deploymentDate
              ? m.deploymentDate.split("T")[0]
              : new Date().toISOString().split("T")[0],
          };
        }
      );
      if (restoredEntries && restoredEntries.length > 0) {
        setMachineEntries(restoredEntries);
        // Save to localStorage for future use
        const updatedData = {
          ...savedData,
          machineSetup: restoredEntries,
        };
        localStorage.setItem(
          INSTALLATION_JOB_STORAGE_KEY,
          JSON.stringify(updatedData)
        );
      } else {
        // No machines in DB, start fresh
        setMachineEntries([
          {
            id: 1,
            selectedMachine: null,
            newMachineName: "",
            deploymentDate: new Date().toISOString().split("T")[0],
          },
        ]);
      }
    } else if (!savedData.machineSetup || savedData.machineSetup.length === 0) {
      setMachineEntries([
        {
          id: 1,
          selectedMachine: null,
          newMachineName: "",
          deploymentDate: new Date().toISOString().split("T")[0],
        },
      ]);
    }
  }, [machinesDB, jobStatus, installationJobDB, locationDB]);

  /** Save to localStorage */
  useEffect(() => {
    const storedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}"
    );
    localStorage.setItem(
      INSTALLATION_JOB_STORAGE_KEY,
      JSON.stringify({
        ...storedData,
        machineSetup: machineEntries,
      })
    );
  }, [machineEntries]);

  useEffect(() => {
    const storedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}"
    );
    localStorage.setItem(
      INSTALLATION_JOB_STORAGE_KEY,
      JSON.stringify({
        ...storedData,
        thirdPartyDetail: {
            name: thirdPartyName,
            email: thirdPartyEmail,
          }
      })
    );
  }, [thirdPartyEmail, thirdPartyName]);

  /** Handlers */
  const handleAutoGenerateName = (index: number) => {
    const updated = [...machineEntries];
    updated[index].newMachineName = location;
    setMachineEntries(updated);
  };

  const handleMachineChange = (index: number, machineId: string) => {
    const updated = [...machineEntries];
    const machine = machinesDB.find((m) => m.id === machineId) || null;
    updated[index].selectedMachine = machine;
    updated[index].selectedMachineId = machineId;
    updated[index].machineError = ""; // clear error
    setMachineEntries(updated);
    handleAutoGenerateName(index);
  };

  const handleNewMachineNameChange = (index: number, value: string) => {
    const updated = [...machineEntries];
    updated[index].newMachineName = value.toLowerCase();
    setMachineEntries(updated);
  };

  const handleDeploymentDateChange = (index: number, value: string) => {
    const updated = [...machineEntries];
    updated[index].deploymentDate = value;
    updated[index].deploymentDateError = value
      ? ""
      : "Deployment date is required";
    setMachineEntries(updated);
  };

  const handleNext = async () => {
    if (machineEntries.length === 0) {
      toast.error("Please add at least one machine.");
      return;
    }
    const updated = [...machineEntries];
    let hasError = false;

    updated.forEach((entry) => {
      if (!entry.selectedMachine) {
        entry.machineError = "Please select a machine";
        toast.error("Please select a machine.");
        hasError = true;
      }

      if (!entry.deploymentDate) {
        entry.deploymentDateError = "Deployment date is required";
        toast.error("Please enter deployment date.");
        hasError = true;
      }
    });

    if (thirdPartyEmail) {
      if (!thirdPartyEmail.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) {
        toast.error("Please enter a valid email address");
        hasError = true;
      } else if (thirdPartyName === "") {
        toast.error("Please enter a name");
        hasError = true;
      } else {
        setThirdPartyEmailError("");
        setThirdPartyNameError("");
      }
    } else {
      setThirdPartyEmailError("");
    }

    setMachineEntries(updated);
    if (!hasError) {
      setIsFormSubmitted(true);
      const selectedMachines = machineEntries.map((entry) => {
        const machine = machinesDB.find(
          (m) => m.id === entry.selectedMachineId
        );
        if (!machine) return;
        return {
          id: entry.selectedMachineId!,
          serialNumber: machine.serial_number,
          currentMachineName: machine.machine_name,
          newMachineName: entry.newMachineName,
          deploymentDate: entry.deploymentDate,
        };
      });

      const result = await installationJobMachineSetup(jobId, {
        locationId: storedData.locationSelection?.id,
        machines: selectedMachines.filter(
          (m): m is NonNullable<typeof m> => m !== undefined
        ),
        ...(thirdPartyEmail && {
          thirdPartyDetail: {
            name: thirdPartyName,
            email: thirdPartyEmail,
          },
        }),
      });

      if (result?.isSuccess) {
        localStorage.removeItem(INSTALLATION_JOB_STORAGE_KEY);
        localStorage.removeItem(INSTALLATION_JOB_STEP_STORAGE_KEY);
        toast.success("Machine setup saved successfully.");
        navigate(`/installation-job/${jobId}`);
        //setCurrentStep(InstalltionFormSteps.MACHINE_DATA);
        setIsFormSubmitted(false);
      } else {
        toast.error(result.message);
        setIsFormSubmitted(false);
      }
    }
  };

  const handleAddMachine = () => {
    setMachineEntries([
      ...machineEntries,
      {
        id: machineEntries.length + 1,
        selectedMachine: null,
        newMachineName: "",
        deploymentDate: new Date().toISOString().split("T")[0],
      },
    ]);
  };

  const handleRemoveMachine = (id: number) => {
    if (machineEntries.length === 1) return;
    setMachineEntries(machineEntries.filter((entry) => entry.id !== id));
  };

  const handleBack = () => setCurrentStep(InstalltionFormSteps.CHOOSE_LOCATION);

  const API_URL = process.env.REACT_APP_FETCH_API_URL;
  const reloadMachines = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/tickets/get-replace-machines`
      );
      const data = await response.json();
      setLocalMachinesDB(data.data);
    } catch (error) {
      console.error("Failed to reload machines");
    }
  };

  const handleModalClose = () => setShowModal(false);
  const handleModalShow = () => setShowModal(true);

  const handleSaveThirdPartyDetails = () => {
    if (!thirdPartyEmail.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) {
      setThirdPartyEmailError("Please enter a valid email address");
      return;
    }
    if (thirdPartyName.trim() === "") {
      setThirdPartyNameError("Please enter a name");
      return;
    }
    setThirdPartyEmailError("");
    setThirdPartyNameError("");
    setShowModal(false);
  };

  /** UI */
  return (
    <div>
      <header className="header">
        <h2>Machine Setup</h2>
      </header>
      <div className="job-wrapper">
        
        <div className="mb-3 d-flex" style={{justifyContent: "space-between"}}>
           <button className="btn btn-primary " type="button" onClick={handleModalShow}>
            Add Third Party
          </button>
          <button
            type="button"
            className="btn btn-primary me-2 "
            onClick={handleAddMachine}
          >
            + Add Machine
          </button>
        </div>
        {machineEntries.length === 0 && (
          <div className="text-center text-muted">
            No machines added. Please click "Add Machine" to begin.
          </div>
        )}
        {machineEntries.map((entry, index) => (
          <div key={entry.id} className="mt-8 card p-4 mb-4 shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold">Machine {index + 1}</h5>
              {machineEntries.length > 1 && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handleRemoveMachine(entry.id)}
                >
                  <i className="fa fa-times" aria-hidden="true"></i>
                </button>
              )}
            </div>

            {/* Serial Number */}
            <div className="col-12 mb-4">
              <label className="wo-label fs-5 fw-bold machine-serial-label">
                Choose Machine Serial Number:
              </label>
              <Tooltip
                anchorSelect=".machine-serial-label"
                data-placement="top"
              >
                Select a machine serial number. Only machines with 'New – Ready
                for Deployment' and 'Refurbished – Ready for Deployment'
                statuses are shown
              </Tooltip>
              <div className="d-flex align-items-center">
                <Select
                  isSearchable
                  className={clsx("react-select", "flex-grow-1")}
                  classNamePrefix="my-react-select"
                  placeholder="Select Machine Serial"
                  options={localMachinesDB.map((machine) => ({
                    value: machine.id,
                    label: `${machine.serial_number} (${
                      statusMap[machine.machine_status]
                    })`,
                  }))}
                  value={
                    entry.selectedMachine
                      ? {
                          value: entry.selectedMachine.id,
                          label: `${entry.selectedMachine.serial_number} (${
                            statusMap[entry.selectedMachine.machine_status]
                          })`,
                        }
                      : null
                  }
                  onChange={(option: any) =>
                    handleMachineChange(index, option.value)
                  }
                  isOptionDisabled={(option) =>
                    machineEntries.some(
                      (e, idx) =>
                        idx !== index && e.selectedMachine?.id === option.value
                    )
                  }
                />
                <button
                  type="button"
                  className="btn btn-secondary ms-2"
                  onClick={reloadMachines}
                >
                  <i className="fa fa-refresh" aria-hidden="true"></i>
                </button>
              </div>
              {entry.machineError && (
                <small className="text-danger">{entry.machineError}</small>
              )}
              <small className="text-muted d-block mt-1">
                Note: machines with the statuses “New – Ready for Deployment”
                and “Refurbished – Ready for Deployment” will be displayed in
                the dropdown.
              </small>
            </div>

            {/* Current Machine Name */}
            <div className="col-12 mb-4">
              <label className="wo-label fs-5 fw-bold current-machine-name-label">
                Current Machine Name:
              </label>
              <Tooltip
                anchorSelect=".current-machine-name-label"
                data-placement="top"
              >
                The existing name of the machine in the system
              </Tooltip>
              <input
                type="text"
                className="form-control"
                value={entry.selectedMachine?.machine_name || ""}
                readOnly
                style={{ backgroundColor: "#f0f8ff" }}
              />
            </div>

            {/* New Machine Name */}
            <div className="col-12 mb-4">
              <label className="wo-label fs-5 fw-bold new-machine-name-label">
                New Machine Name:
              </label>
              <Tooltip
                anchorSelect=".new-machine-name-label"
                data-placement="top"
              >
                Auto-generated name based on location. You can edit this if
                needed
              </Tooltip>
              <input
                type="text"
                className="form-control"
                value={entry.newMachineName}
                onChange={(e) =>
                  handleNewMachineNameChange(index, e.target.value)
                }
                style={{
                  backgroundColor: "#fffbea",
                  textTransform: "lowercase",
                }}
              />
              <small className="text-muted">
                Auto-generated as <strong>Machine Name</strong>. You can edit if
                needed.
              </small>
            </div>

            {/* Deployment Date */}
            <div className="col-12 mb-4">
              <label className="wo-label fs-5 fw-bold deployment-date-label">
                Deployment Date <span className="text-danger">*</span>
              </label>
              <Tooltip
                anchorSelect=".deployment-date-label"
                data-placement="top"
              >
                The date when the machine will be deployed at this location
              </Tooltip>
              <Datetime
                dateFormat="YYYY-MM-DD"
                timeFormat={false}
                closeOnSelect
                value={entry.deploymentDate}
                onChange={(date: any) => {
                  let formatted = "";
                  if (date && typeof date.format === "function")
                    formatted = date.format("YYYY-MM-DD");
                  handleDeploymentDateChange(index, formatted);
                }}
                inputProps={{ placeholder: "Select Deployment Date" }}
              />
              {entry.deploymentDateError && (
                <small className="text-danger">
                  {entry.deploymentDateError}
                </small>
              )}
            </div>
          </div>
        ))}

        {/* Third Party Details Button */}
        <div className="col-12 mb-4">
          {thirdPartyEmail && thirdPartyName && (
            <div className="mt-3">
              <strong>Third Party Details:</strong>
              <p style={{marginBottom: 0}}><strong>Email:</strong> {thirdPartyEmail}</p>
              <p style={{marginBottom: 0}}><strong>Name:</strong> {thirdPartyName}</p>
            </div>
          )}
        </div>

        {/* Modal for Third Party Details */}
        <Modal show={showModal} onHide={handleModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Enter Third Party Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            
            <div className="mb-3">
              <label className="form-label">Third Party Name</label>
              <input
                type="text"
                className="form-control"
                value={thirdPartyName}
                onChange={(e) => setThirdPartyName(e.target.value)}
                placeholder="Enter third party name"
              />
              {thirdPartyNameError && (
                <small className="text-danger">{thirdPartyNameError}</small>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Third Party Email</label>
              <input
                type="email"
                className="form-control"
                value={thirdPartyEmail}
                onChange={(e) => setThirdPartyEmail(e.target.value)}
                placeholder="Enter third party email"
              />
              {thirdPartyEmailError && (
                <small className="text-danger">{thirdPartyEmailError}</small>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" type="button" onClick={handleModalClose}>
              Close
            </button>
            <button className="btn btn-primary" type="button" onClick={handleSaveThirdPartyDetails}>
              Save Details
            </button>
          </Modal.Footer>
        </Modal>

        {/* <div className="d-flex justify-content-between mt-3">
            <div className="left-icon mb-4 mt-4 ms-2" onClick={handleBack} style={{ cursor: "pointer" }}>
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
        </div>

        <button type="button" className="btn btn-primary" onClick={handleNext}>Next →</button>
       */}
      </div>
      <SimpleMobileStepper
        totalSteps={3}
        handleNext={handleNext}
        handleBack={handleBack}
        isFormSubmitted={isFormSubmitted}
        isLastStep={true}
        activeStep={InstallationJobStepsList.findIndex(
          (s) => s.id === InstalltionFormSteps.MACHINE_SETUP
        )}
      />
    </div>
  );
};
